import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import {
  buildClientContext,
  buildErrorPayload,
  isNoiseError,
  normalizeError,
  shouldReport,
  unwrapError,
} from './error-normalizer';
import { extractHost, normalizeUrlToTemplate } from './event-tracker.interceptor';
import { EventTrackerService } from './event-tracker.service';

const CHUNK_RELOAD_KEY = 'rt_chunk_reload';

/** Minimum window between automatic reloads caused by a ChunkLoadError. */
const CHUNK_RELOAD_COOLDOWN_MS = 30_000;

/** In-memory fallback of the reload guard (sessionStorage may throw in some WebViews). */
let lastChunkReloadAt = 0;

/** Global ErrorHandler that reports `js_error_captured` events. Port of legacy EventTrackerErrorHandler. */
@Injectable()
export class EventTrackerErrorHandler implements ErrorHandler {
  private readonly tracker = inject(EventTrackerService);

  handleError(error: unknown): void {
    console.error(error);

    if (this.isChunkLoadError(error)) {
      this.handleChunkLoadError(error);
      return;
    }

    // HTTP failures are already tracked by eventTrackerInterceptor.
    if (error instanceof HttpErrorResponse) {
      this.trackUncaughtHttpError(error);
      return;
    }

    const normalized = normalizeError(unwrapError(error));
    if (isNoiseError(normalized) || !shouldReport(normalized)) return;

    this.tracker.track({
      event_type: 'js_error',
      event_name: 'js_error_captured',
      result: 'fail',
      error_kind: 'exception',
      payload: buildErrorPayload(normalized),
    });
  }

  private handleChunkLoadError(error: unknown): void {
    const normalized = normalizeError(unwrapError(error));
    const now = Date.now();
    const lastReload = Math.max(this.readLastReload(), lastChunkReloadAt);

    if (now - lastReload > CHUNK_RELOAD_COOLDOWN_MS) {
      lastChunkReloadAt = now;
      this.writeLastReload(now);

      this.tracker.trackImmediate(
        {
          event_type: 'js_error',
          event_name: 'js_error_captured',
          result: 'fail',
          error_kind: 'unknown',
          payload: {
            ...buildErrorPayload(normalized),
            error_type: 'ChunkLoadError',
            stack_hash: 'chunk_reload_triggered',
          },
        },
        true,
      );

      window.location.reload();
      return;
    }

    this.tracker.track({
      event_type: 'js_error',
      event_name: 'js_error_captured',
      result: 'fail',
      error_kind: 'unknown',
      payload: {
        ...buildErrorPayload(normalized),
        error_type: 'ChunkLoadError',
        chunk_reload_failed: true,
      },
    });
  }

  private trackUncaughtHttpError(error: HttpErrorResponse): void {
    this.tracker.track({
      event_type: 'js_error',
      event_name: 'js_error_uncaught_http',
      result: 'fail',
      status_code: error.status,
      error_kind: error.status === 0 ? 'network' : 'http_error',
      payload: {
        http_status: error.status,
        url_host: extractHost(error.url ?? ''),
        route_template: normalizeUrlToTemplate(error.url ?? ''),
        error_message: error.message?.substring(0, 500),
        ...buildClientContext(),
      },
    });
  }

  private isChunkLoadError(error: unknown): boolean {
    const e = error as { name?: unknown; message?: unknown } | null;
    if (e?.name === 'ChunkLoadError') return true;

    const message = typeof e?.message === 'string' ? e.message : '';
    return (
      /loading (css )?chunk/i.test(message) ||
      /(failed to fetch|error loading) dynamically imported module/i.test(message) ||
      /importing a module script failed/i.test(message)
    );
  }

  private readLastReload(): number {
    const raw = this.sessionGet(CHUNK_RELOAD_KEY);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private writeLastReload(timestamp: number): void {
    this.sessionSet(CHUNK_RELOAD_KEY, String(timestamp));
  }

  private sessionGet(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private sessionSet(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      /* ignored */
    }
  }
}
